<template>
  <div class="container-fluid">
    <div class="page-wrapper">
      <div slot="header" class="page-wrapper-header">
        <div class="row">
          <div class="col-6">
            <h3 class="mb-0">{{ $t("MAIL_V2.MAIL_TEMPLATES") }}</h3>
          </div>
          <div class="col-6 text-right">
            <base-button
              class="elite-button add"
              icon
              size="sm"
              @click="openTemplateMailCreateModal"
              v-if="$currentUserCan($permissions.PERM_CREATE_TEMPLATES_MAILS)"
            >
              <span class="btn-inner--icon">
                <i class="far fa-plus-circle"></i>
              </span>
              <span class="btn-inner--text">{{ $t("MAIL_V2.ADD_TEMPLATE_MAIL") }}</span>
            </base-button>
            <notification-subscription
              v-if="$currentUserCan($permissions.PERM_VIEW_TEMPLATES_MAILS)"
              :objectType="'templates'"
              :events="{
                CREATE: $t('NOTIFICATIONS.EVENT_CREATE'),
                UPDATE: $t('NOTIFICATIONS.EVENT_UPDATE'),
                DELETE: $t('NOTIFICATIONS.EVENT_DELETE'),
              }"
            />
          </div>
        </div>
      </div>

      <div class="page-wrapper-table">
        <div class="page-wrapper-table-header">
        <el-select
          class="select-primary pagination-select mb-2"
          v-model="pagination.perPage"
          :placeholder="$t('COMMON.PER_PAGE')"
        >
          <el-option
            class="select-primary"
            v-for="item in pagination.perPageOptions"
            :key="item"
            :label="item"
            :value="item"
          >
          </el-option>
        </el-select>

          <reseller-selector
            v-if="
              $currentUserCan($permissions.PERM_VIEW_ANY_RESELLERS)
            "
            @resellerChanged="(resellerId) => (selectedReseller = resellerId)"
          />

          <organization-selector
            v-if="
              $currentUserCan($permissions.PERM_VIEW_ANY_ORGANIZATIONS)
            "
            :filterReseller="selectedReseller"
            @organizationChanged="(id) => { selectedOrganization = id; }"
          />

          <el-select v-model="selectedCategory" :placeholder="$t('MAIL_V2.ALL_CATEGORIES')" clearable>
            <el-option value="transactional" :label="$t('MAIL_V2.TRANSACTIONAL')" />
            <el-option value="notification" :label="$t('MAIL_V2.NOTIFICATION')" />
            <el-option value="marketing" :label="$t('MAIL_V2.MARKETING')" />
          </el-select>

          <el-select v-model="selectedStatus" :placeholder="$t('MAIL_V2.ALL_STATUS')" clearable>
            <el-option value="customized" :label="$t('MAIL_V2.CUSTOMIZED')" />
            <el-option value="default" :label="$t('MAIL_V2.USING_DEFAULT')" />
          </el-select>

          <base-input
            v-model="query"
            type="search"
            prepend-icon="fas fa-search"
            :placeholder="$t('COMMON.SEARCH')"
            clearable
          />
        </div>

        <div class="page-wrapper-table-body">
          <div class="page-wrapper-table-body-inner">
            <el-table
              class="align-items-center table-flush"
              header-row-class-name="thead-light"
              :data="allTypes"
              @sort-change="sortChange"
            >
              <div class="table-loading" slot="empty" v-if="loading">
                <img src="/img/loading.gif" />
              </div>

              <el-table-column :label="$t('MAIL_V2.TYPE')" min-width="220" sortable="custom" prop="type_name">
                <template v-slot="{ row }">
                  <div>
                    <strong>{{ getLocale(row.type_name) }}</strong>
                    <br />
                    <span class="text-muted" style="font-size:11px;font-family:monospace;">{{ row.code }}</span>
                  </div>
                </template>
              </el-table-column>

              <el-table-column :label="$t('MAIL_V2.CATEGORY')" min-width="130">
                <template v-slot="{ row }">
                  <span class="badge-cat" :class="'badge-cat--' + row.category">
                    {{ $t("MAIL_V2." + row.category.toUpperCase()) }}
                  </span>
                </template>
              </el-table-column>

              <el-table-column :label="$t('MAIL_V2.STATUS')" min-width="140" v-if="selectedOrganization">
                <template v-slot="{ row }">
                  <span class="badge-st" :class="row.is_customized ? 'badge-st--custom' : 'badge-st--default'">
                    {{ row.is_customized ? $t("MAIL_V2.CUSTOMIZED") : $t("MAIL_V2.USING_DEFAULT") }}
                  </span>
                </template>
              </el-table-column>

              <el-table-column :label="$t('MAIL_V2.OVERRIDES')" min-width="120" v-if="!selectedOrganization">
                <template v-slot="{ row }">
                  <span v-if="row.overrides_count > 0" class="badge-st badge-st--custom">
                    {{ row.overrides_count }} {{ row.overrides_count > 1 ? 'orgs' : 'org' }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </template>
              </el-table-column>

              <el-table-column :label="$t('MAIL_V2.LANGUAGES')" min-width="150">
                <template v-slot="{ row }">
                  <span v-for="l in row.available_locales" :key="l" class="locale-chip mr-1">{{ l.toUpperCase() }}</span>
                </template>
              </el-table-column>

              <el-table-column :label="$t('MAIL_V2.VARIABLES')" min-width="150">
                <template v-slot="{ row }">
                  {{ (row.variables || []).length }}
                </template>
              </el-table-column>
              <el-table-column fixed="right" min-width="180">
                <div slot-scope="{ row }" class="table-actions">
                  <!-- Always: Edit default template (reseller level) -->
                  <el-tooltip :content="$t('MAIL_V2.EDIT_DEFAULT')" placement="top">
                    <a type="text" @click="editDefault(row)" class="table-action" style="cursor:pointer;">
                      <i class="fal fa-edit"></i>
                    </a>
                  </el-tooltip>

                  <!-- When org selected + customized: Edit org override -->
                  <el-tooltip
                    v-if="selectedOrganization && row.is_customized && row.template"
                    :content="$t('MAIL_V2.EDIT_CUSTOMIZATION')"
                    placement="top"
                  >
                    <a type="text" @click="editOrgOverride(row)" class="table-action" style="cursor:pointer;color:#e65100;">
                      <i class="fal fa-pencil"></i>
                    </a>
                  </el-tooltip>
                </div>
              </el-table-column>
            </el-table>
          </div>
        </div>

        <div class="page-wrapper-table-footer">
          <p class="card-category">
            {{ $t("COMMON.DISPLAY_FROM_X_TO_X_OF_X_ENTRIES", { from: total ? from + 1 : 0, to, of: total }) }}
          </p>
          <base-pagination
            class="pagination-no-border"
            v-model="pagination.currentPage"
            :per-page="pagination.perPage"
            :total="total"
          />
        </div>
      </div>

      <!-- Edit Default Template Modal (reseller level) -->
      <div v-if="isDefaultEditOpened" class="resizable-wrapper show" :key="'default-' + renderKey">
        <div class="resizable-wrapper-content w-100" v-resizable-modal="'MAIL_V2_DEFAULT_EDIT'">
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                <i class="fal fa-layer-group mr-2"></i>
                {{ $t("MAIL_V2.EDIT_DEFAULT") }} — {{ getLocale(editingItem?.type_name) }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <button class="close" @click="closeModal"><i class="fal fa-times"></i></button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body mv2-editor-body">
            <mail-v2-editor
              v-if="editingItem && defaultTemplateData"
              :template-data="defaultTemplateData"
              :mail-type="editingItem"
              :organization-id="0"
              :is-default-edit="true"
              @saved="onDefaultSaved"
              @cancel="closeModal"
            />
          </div>
        </div>
      </div>

      <!-- Edit Org Override Modal -->
      <div v-if="isOrgEditOpened" class="resizable-wrapper show" :key="'org-' + renderKey">
        <div class="resizable-wrapper-content w-100" v-resizable-modal="'MAIL_V2_ORG_EDIT'">
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1>
                <i class="fal fa-pen-fancy mr-2"></i>
                {{ $t("MAIL_V2.EDIT_CUSTOMIZATION") }} — {{ getLocale(editingItem?.type_name) }}
              </h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <button class="close" @click="closeModal"><i class="fal fa-times"></i></button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body mv2-editor-body">
            <mail-v2-editor
              v-if="editingItem && editingItem.template"
              :template-data="editingItem.template"
              :mail-type="editingItem"
              :organization-id="selectedOrganization"
              :organization="editingItem.organization"
              @saved="onSaved"
              @cancel="closeModal"
            />
          </div>
        </div>
      </div>
      <!-- Create Mail Type Drawer -->
      <div v-if="isCreateOpened" class="resizable-wrapper show" :key="'create-' + renderKey">
        <div class="resizable-wrapper-content" v-resizable-modal="'MAIL_V2_CREATE'">
          <div class="resizable-wrapper-content-header">
            <div class="resizable-wrapper-content-header-left">
              <h1><i class="fal fa-plus-circle mr-2"></i> {{ $t("MAIL_V2.ADD_TEMPLATE_MAIL") }}</h1>
            </div>
            <div class="resizable-wrapper-content-header-right">
              <button class="close" @click="isCreateOpened = false"><i class="fal fa-times"></i></button>
            </div>
          </div>
          <div class="resizable-wrapper-content-body">
            <form class="add-form" @submit.prevent="submitCreate" @keydown.enter.prevent="() => {}">

              <div class="form-wrapper"
                v-if="$currentUserCan($permissions.PERM_VIEW_ANY_RESELLERS)"
              >
                <base-input
                  :label="`${$t('COMMON.RESELLER')} (*)`"
                  :placeholder="$t('COMMON.RESELLER')"
                >
                  <reseller-selector
                    :filterable="true"
                    :showAll="false"
                    @resellerChanged="(id) => { createForm.reseller_id = id; clearError('reseller_id'); }"
                  />
                </base-input>
                <div v-if="createErrors.reseller_id" class="invalid-feedback" style="display: block;">
                  {{ createErrors.reseller_id }}
                </div>
              </div>

              <div class="form-wrapper">
                <base-input
                  :label="`${$t('MAIL_V2.CODE')} (*)`"
                  :placeholder="'WELCOME'"
                  v-model="createForm.code"
                  :class="{ 'has-danger': createErrors.code }"
                  @input="clearError('code')"
                />
                <div v-if="createErrors.code" class="invalid-feedback" style="display: block;">
                  {{ createErrors.code }}
                </div>
              </div>

              <div class="form-wrapper">
                <base-input
                  :label="$t('MAIL_V2.CATEGORY')"
                  :placeholder="$t('MAIL_V2.CATEGORY')"
                >
                  <el-select v-model="createForm.category" :filterable="true">
                    <el-option value="transactional" :label="$t('MAIL_V2.TRANSACTIONAL')" />
                    <el-option value="notification" :label="$t('MAIL_V2.NOTIFICATION')" />
                    <el-option value="marketing" :label="$t('MAIL_V2.MARKETING')" />
                  </el-select>
                </base-input>
              </div>

              <div class="form-wrapper">
                <base-input
                  :label="`${$t('MAIL_V2.LANGUAGES')} (*)`"
                  :placeholder="$t('MAIL_V2.LANGUAGES')"
                >
                  <el-select
                    v-model="createForm.available_locales"
                    multiple
                    :filterable="true"
                    @change="onCreateLocalesChanged"
                  >
                    <el-option value="fr" label="Français (FR)" />
                    <el-option value="en" label="English (EN)" />
                    <el-option value="es" label="Español (ES)" />
                    <el-option value="pt" label="Português (PT)" />
                    <el-option value="ar" label="العربية (AR)" />
                  </el-select>
                </base-input>
                <div v-if="createErrors.available_locales" class="invalid-feedback" style="display: block;">
                  {{ createErrors.available_locales }}
                </div>
              </div>

              <!-- Nom + Description par langue sélectionnée -->
              <div
                v-for="locale in createForm.available_locales"
                :key="'name-' + locale"
                class="form-wrapper full"
              >
                <div class="mb-2">
                  <span class="badge badge-info">{{ locale.toUpperCase() }}</span>
                </div>
                <div class="row">
                  <div class="col-6">
                    <base-input
                      :label="`${$t('COMMON.NAME')} (*)`"
                      :placeholder="$t('COMMON.NAME')"
                      v-model="createForm.name[locale]"
                      :class="{ 'has-danger': createErrors['name_' + locale] }"
                      @input="clearError('name_' + locale)"
                    />
                    <div v-if="createErrors['name_' + locale]" class="invalid-feedback" style="display: block;">
                      {{ createErrors['name_' + locale] }}
                    </div>
                  </div>
                  <div class="col-6">
                    <base-input
                      :label="$t('MAIL_V2.DESCRIPTION')"
                      :placeholder="$t('MAIL_V2.DESCRIPTION')"
                    >
                      <textarea class="form-control" v-model="createForm.description[locale]" rows="2"></textarea>
                    </base-input>
                  </div>
                </div>
              </div>

              <!-- Variables -->
              <div class="col-12 mt-3 mb-2">
                <div class="d-flex align-items-center justify-content-between">
                  <h3 class="mb-0">{{ $t("MAIL_V2.VARIABLES") }}</h3>
                  <base-button class="elite-button add" icon size="sm" @click="addVariable">
                    <span class="btn-inner--icon"><i class="far fa-plus-circle"></i></span>
                    <span class="btn-inner--text">{{ $t("MAIL_V2.ADD_VARIABLE") }}</span>
                  </base-button>
                </div>
              </div>

              <div v-if="!createForm.variables.length" class="form-wrapper full">
                <p class="text-muted mb-0">{{ $t("MAIL_V2.NO_VARIABLES_YET") }}</p>
              </div>

              <div
                v-for="(variable, idx) in createForm.variables"
                :key="'var-' + idx"
                class="variable-entry"
              >
                <div class="form-wrapper">
                  <base-input
                    label="Key (*)"
                    v-model="variable.key"
                    placeholder="customer_name"
                    :class="{ 'has-danger': createErrors['variables_' + idx + '_key'] }"
                    @input="clearError('variables_' + idx + '_key')"
                  />
                  <div v-if="createErrors['variables_' + idx + '_key']" class="invalid-feedback" style="display: block;">
                    {{ createErrors['variables_' + idx + '_key'] }}
                  </div>
                </div>
                <div class="form-wrapper">
                  <base-input
                    :label="`Label (${(createForm.available_locales[0] || 'fr').toUpperCase()})`"
                    v-model="variable.label[createForm.available_locales[0] || 'fr']"
                    placeholder="Nom du client"
                  />
                </div>
                <div class="form-wrapper">
                  <base-input :label="$t('MAIL_V2.GROUP')" v-model="variable.group" placeholder="customer" />
                </div>
                <div class="form-wrapper">
                  <base-input label="Sample" v-model="variable.sample_value" placeholder="Jean Dupont" />
                </div>
                <div class="form-wrapper variable-entry-delete">
                  <a @click="createForm.variables.splice(idx, 1)" class="text-danger" style="cursor:pointer;">
                    <i class="fal fa-trash-alt"></i>
                  </a>
                </div>
              </div>

              <div class="submit-wrapper">
                <base-button
                  type="button"
                  class="btn btn-sm elite-submit"
                  native-type="submit"
                  :disabled="createLoading"
                >
                  <i class="fas fa-spinner fa-spin" v-if="createLoading"></i>
                  {{ $t("MAIL_V2.ADD_TEMPLATE_MAIL") }}
                </base-button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import { Table, TableColumn, Select, Option, Tooltip } from "element-ui";
import { mapGetters } from "vuex";
import { BasePagination } from "@/components";
import BaseInput from "@/components/Inputs/BaseInput.vue";
import OrganizationSelector from "@/components/OrganizationSelector.vue";
import ResellerSelector from "@/components/ResellerSelector.vue";
import NotificationSubscription from "@/components/NotificationSubscription.vue";
import MailV2Editor from "./partials/MailV2Editor.vue";

export default {
  components: {
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Tooltip.name]: Tooltip,
    BasePagination,
    BaseInput,
    OrganizationSelector,
    ResellerSelector,
    NotificationSubscription,
    MailV2Editor,
  },

  data() {
    return {
      query: "",
      selectedReseller: null,
      selectedOrganization: null,
      selectedCategory: null,
      selectedStatus: null,
      sort: "-created_at",
      loading: false,
      isDefaultEditOpened: false,
      isOrgEditOpened: false,
      isCreateOpened: false,
      createLoading: false,
      createErrors: {},
      createForm: {
        reseller_id: null,
        code: "",
        name: {},
        description: {},
        category: "transactional",
        available_locales: ["fr", "en"],
        variables: [],
      },
      editingItem: null,
      defaultTemplateData: null,
      renderKey: 1,
      pagination: {
        perPage: 20,
        currentPage: 1,
        perPageOptions: [20, 50, 100],
      },
    };
  },

  computed: {
    ...mapGetters("mailV2", { allTypes: "allTypes", allTypesMeta: "allTypesMeta" }),

    total() { return this.allTypesMeta?.total || 0; },
    from() { return (this.pagination.currentPage - 1) * this.pagination.perPage; },
    to() { return Math.min(this.from + this.pagination.perPage, this.total); },
  },

  watch: {
    "pagination.currentPage"() { this.getList(); },
    "pagination.perPage"() { this.pagination.currentPage = 1; this.getList(); },
    selectedReseller() { this.pagination.currentPage = 1; this.getList(); },
    selectedOrganization() { this.pagination.currentPage = 1; this.getList(); },
    selectedCategory() { this.pagination.currentPage = 1; this.getList(); },
    selectedStatus() { this.pagination.currentPage = 1; this.getList(); },
    query: _.debounce(function () { this.pagination.currentPage = 1; this.getList(); }, 300),
  },

  mounted() {
    this.getList();
  },

  methods: {
    async getList() {
      this.loading = true;
      try {
        const params = {
          sort: this.sort,
          filter: {},
          page: {
            number: this.pagination.currentPage,
            size: this.pagination.perPage,
          },
        };

        if (this.selectedReseller) params.filter.reseller = this.selectedReseller;
        if (this.selectedOrganization) params.filter.organization = this.selectedOrganization;
        if (this.selectedCategory) params.filter.category = this.selectedCategory;
        if (this.selectedStatus) params.filter.status = this.selectedStatus;
        if (this.query) params.filter.search = this.query;

        await this.$store.dispatch("mailV2/listAllTypes", { params });
      } finally {
        this.loading = false;
      }
    },

    sortChange({ prop, order }) {
      if (prop && order) {
        this.sort = order === "descending" ? `-${prop}` : prop;
      } else {
        this.sort = "-created_at";
      }
      this.getList();
    },

    openTemplateMailCreateModal() {
      const defaultLocales = ["fr", "en"];
      const name = {};
      const description = {};
      defaultLocales.forEach((l) => { name[l] = ""; description[l] = ""; });

      this.createForm = {
        reseller_id: null,
        code: "",
        name,
        description,
        category: "transactional",
        available_locales: defaultLocales,
        variables: [],
      };
      this.createErrors = {};
      this.isCreateOpened = true;
      this.renderKey++;
    },

    onCreateLocalesChanged(newLocales) {
      this.clearError("available_locales");
      // Ensure name/description have reactive keys for all selected locales
      newLocales.forEach((l) => {
        if (this.createForm.name[l] === undefined) {
          this.$set(this.createForm.name, l, "");
        }
        if (this.createForm.description[l] === undefined) {
          this.$set(this.createForm.description, l, "");
        }
      });
    },

    clearError(field) {
      if (this.createErrors[field]) {
        this.$set(this.createErrors, field, null);
      }
    },

    validateCreateForm() {
      const errors = {};

      if (!this.createForm.reseller_id) {
        errors.reseller_id = this.$t("MAIL_V2.FIELD_REQUIRED");
      }
      if (!this.createForm.code || !this.createForm.code.trim()) {
        errors.code = this.$t("MAIL_V2.FIELD_REQUIRED");
      }
      if (!this.createForm.available_locales || !this.createForm.available_locales.length) {
        errors.available_locales = this.$t("MAIL_V2.FIELD_REQUIRED");
      }

      // Validate name for each selected locale
      this.createForm.available_locales.forEach((locale) => {
        if (!this.createForm.name[locale] || !this.createForm.name[locale].trim()) {
          errors["name_" + locale] = this.$t("MAIL_V2.NAME_REQUIRED_FOR", { locale: locale.toUpperCase() });
        }
      });

      // Validate variable keys
      this.createForm.variables.forEach((v, idx) => {
        if (!v.key || !v.key.trim()) {
          errors["variables_" + idx + "_key"] = this.$t("MAIL_V2.FIELD_REQUIRED");
        }
      });

      this.createErrors = errors;
      return Object.keys(errors).length === 0;
    },

    parseBackendErrors(error) {
      const backendErrors = error?.response?.data?.errors || {};
      const parsed = {};

      Object.keys(backendErrors).forEach((key) => {
        const msg = Array.isArray(backendErrors[key]) ? backendErrors[key][0] : backendErrors[key];
        // Map backend keys to our field keys
        // e.g. "name.fr" → "name_fr", "variables.0.key" → "variables_0_key"
        const mappedKey = key.replace(/\./g, "_");
        parsed[mappedKey] = msg;
      });

      this.createErrors = { ...this.createErrors, ...parsed };
    },

    addVariable() {
      const label = {};
      this.createForm.available_locales.forEach((l) => { label[l] = ""; });
      this.createForm.variables.push({
        key: "",
        label,
        group: "",
        type: "string",
        is_required: false,
        sample_value: "",
      });
    },

    async submitCreate() {
      if (!this.validateCreateForm()) {
        this.$notify({ type: "danger", message: this.$t("MAIL_V2.PLEASE_FIX_ERRORS") });
        return;
      }

      this.createLoading = true;
      try {
        const result = await this.$store.dispatch("mailV2/createMailType", {
          data: this.createForm,
        });
        this.isCreateOpened = false;
        this.getList();
        this.$notify({ type: "success", message: this.$t("MAIL_V2.MAIL_TYPE_CREATED") });

        // Open the default editor for the newly created type
        if (result?.id) {
          this.$nextTick(() => {
            const newRow = this.allTypes.find((t) => t.id === result.id);
            if (newRow) this.editDefault(newRow);
          });
        }
      } catch (error) {
        this.parseBackendErrors(error);
        const msg = error?.response?.data?.message || this.$t("ERRORS.SOMETHING_WENT_WRONG");
        this.$notify({ type: "danger", message: msg });
      } finally {
        this.createLoading = false;
      }
    },

    async editDefault(row) {
      // Load full type data with default_templates
      try {
        const typeData = await this.$store.dispatch("mailV2/getMailType", { typeId: row.id });
        this.editingItem = { ...row, ...typeData };

        // Transform default_templates into a format MailV2Editor understands
        const defaultTemplates = typeData.default_templates || {};
        const subject = {};
        const bodyHtml = {};
        const bodyJson = {};
        (row.available_locales || []).forEach((l) => {
          subject[l] = defaultTemplates[l]?.subject || "";
          bodyHtml[l] = defaultTemplates[l]?.body_html || "";
          bodyJson[l] = defaultTemplates[l]?.body_json || null;
        });

        this.defaultTemplateData = {
          id: row.id,
          subject,
          body_html: bodyHtml,
          body_json: bodyJson,
          metadata: {},
        };

        this.isDefaultEditOpened = true;
        this.renderKey++;
      } catch (error) {
        this.$notify({ type: "danger", message: this.$t("ERRORS.SOMETHING_WENT_WRONG") });
      }
    },

    editOrgOverride(row) {
      this.editingItem = row;
      this.isOrgEditOpened = true;
      this.renderKey++;
    },

    closeModal() {
      this.isDefaultEditOpened = false;
      this.isOrgEditOpened = false;
      this.isCreateOpened = false;
      this.editingItem = null;
      this.defaultTemplateData = null;
    },

    onSaved() {
      this.closeModal();
      this.getList();
      this.$notify({ type: "success", message: this.$t("MAIL_V2.TEMPLATE_SAVED") });
    },

    async onDefaultSaved() {
      this.closeModal();
      this.getList();
      this.$notify({ type: "success", message: this.$t("MAIL_V2.DEFAULT_TEMPLATE_SAVED") });
    },

    getLocale(val) {
      if (!val) return "";
      if (typeof val === "string") return val;
      return val[this.$i18n?.locale || "fr"] || val.fr || val.en || Object.values(val)[0] || "";
    },
  },
};
</script>

<style scoped>
.badge-cat { padding: 3px 10px; border-radius: 4px; font-size: 11px; font-weight: 600; text-transform: uppercase; }
.badge-cat--transactional { background: #e3f2fd; color: #1565c0; }
.badge-cat--notification { background: #fff3e0; color: #e65100; }
.badge-cat--marketing { background: #f3e5f5; color: #7b1fa2; }

.badge-st { padding: 3px 10px; border-radius: 4px; font-size: 11px; font-weight: 600; }
.badge-st--custom { background: #e8f5e9; color: #2e7d32; }
.badge-st--default { background: #f5f5f5; color: #757575; }

.locale-chip { display: inline-block; padding: 2px 6px; background: #f0f0f0; border-radius: 3px; font-size: 10px; font-weight: 700; color: #666; }

/* Variable entry in create form */
.variable-entry {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 8px;
  background: #f9fafb;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}
.variable-entry .form-wrapper { flex: 1; margin-bottom: 0; }
.variable-entry-delete { flex: 0 0 30px; padding-top: 28px; }
</style>
